import { Chart } from 'chart.js';
import { getRelativePosition } from 'chart.js/helpers';

// Shared wind-direction indicator for the weather block (#3254/#3923).
// Draws a rotated arrow per chart tick using the same getPixelForValue
// positioning labelIcon.ts relies on for the weather icon, with the speed
// (e.g. "5 m/s") drawn statically alongside it. chart.js has no built-in
// tooltip for a canvas-drawn, non-dataset region, so hovering the arrow is
// handled via a manual hit-test (chart.js's public getRelativePosition
// helper) + a DOM tooltip, the same technique already used in
// forecast-visitor-bar-chart.component.ts's external tooltip.

interface WindHitbox {
  x: number;
  y: number;
  width: number;
  height: number;
  degree: number;
}

const hitboxCache = new Map<Chart, WindHitbox[]>();
const listenerAttached = new WeakSet<HTMLCanvasElement>();

const getTooltipEl = (chart: Chart): HTMLDivElement => {
  const parent = chart.canvas.parentNode as HTMLElement;
  let el = parent.querySelector<HTMLDivElement>('#wind-arrow-tooltip');
  if (!el) {
    el = document.createElement('div');
    el.id = 'wind-arrow-tooltip';
    el.className = [
      'pointer-events-none',
      'absolute',
      'z-50',
      'bg-neutral-900/95',
      'text-white',
      'text-xs',
      'leading-4',
      'p-2',
      'rounded-md',
      'shadow-lg',
      'whitespace-nowrap',
    ].join(' ');
    el.style.opacity = '0';
    parent.appendChild(el);
  }
  return el;
};

export const windArrowPlugin = {
  id: 'windArrowPlugin',

  afterDatasetsDraw: (chart: Chart) => {
    const winds: { degree: number; speed: number }[] = (chart.data?.datasets as any)?.[0]?.winds;
    if (!winds?.length) {
      hitboxCache.set(chart, []);
      return;
    }

    const xAxis = (chart.scales as any)['x'];
    if (!xAxis) return;

    // mirror the axis's own autoSkip decision, otherwise this row draws one arrow per raw
    // data index and overlaps into unreadable clutter once autoSkip starts hiding tick labels
    const visibleIndices = new Set((xAxis.ticks as { value: number }[])?.map(tick => tick.value));

    const ctx = chart.ctx;
    const arrowLength = 4;
    const arrowFootprint = arrowLength * 2;
    const labelGap = 3;
    const font = '11px sans-serif';
    // icon (labelIcon.ts) bottom is at xAxis.top + 29; precipitation.ts draws
    // its "x.x mm" row in the 4-18px gap below that
    const rowOffset: number = (chart.options as any)?.windArrowPlugin?.rowOffset ?? 29 + 18 + 3;
    const y = xAxis.top + rowOffset + arrowLength;
    const boxes: WindHitbox[] = [];

    ctx.font = font;

    winds.forEach((wind, index) => {
      if (wind == null || wind.degree == null || !visibleIndices.has(index)) return;
      const tickX = xAxis.getPixelForValue(index);
      const label = wind.speed != null ? `${wind.speed} m/s` : '';
      const labelWidth = label ? ctx.measureText(label).width : 0;
      // center the arrow+label group on the tick instead of the arrow alone
      const groupWidth = arrowFootprint + (label ? labelGap + labelWidth : 0);
      const arrowCenterX = tickX - groupWidth / 2 + arrowLength;

      ctx.save();
      ctx.translate(arrowCenterX, y);
      ctx.rotate((wind.degree * Math.PI) / 180);
      ctx.strokeStyle = '#177578';
      ctx.fillStyle = '#177578';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, arrowLength);
      ctx.lineTo(0, -arrowLength);
      ctx.stroke();
      // arrowhead pointing toward the direction the wind blows
      ctx.beginPath();
      ctx.moveTo(0, -arrowLength);
      ctx.lineTo(-2.5, -arrowLength + 4);
      ctx.lineTo(2.5, -arrowLength + 4);
      ctx.closePath();
      ctx.fill();
      ctx.restore();

      if (label) {
        ctx.save();
        ctx.font = font;
        ctx.fillStyle = '#177578';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'middle';
        ctx.fillText(label, arrowCenterX + arrowLength + labelGap, y);
        ctx.restore();
      }

      boxes.push({
        x: arrowCenterX - arrowLength,
        y: y - arrowLength,
        width: arrowLength * 2,
        height: arrowLength * 2,
        degree: wind.degree,
      });
    });

    hitboxCache.set(chart, boxes);

    if (!listenerAttached.has(chart.canvas)) {
      listenerAttached.add(chart.canvas);
      chart.canvas.addEventListener('mousemove', (event: MouseEvent) => {
        const pos = getRelativePosition(event, chart);
        const boxesNow = hitboxCache.get(chart) ?? [];
        const hit = boxesNow.find(b => pos.x >= b.x && pos.x <= b.x + b.width && pos.y >= b.y && pos.y <= b.y + b.height);
        const el = getTooltipEl(chart);

        if (!hit) {
          el.style.opacity = '0';
          return;
        }

        el.innerHTML = `${hit.degree}&deg;`;
        el.style.opacity = '1';
        el.style.left = `${chart.canvas.offsetLeft + hit.x + hit.width / 2}px`;
        el.style.top = `${chart.canvas.offsetTop + hit.y}px`;
        el.style.transform = 'translate(-50%, calc(-100% - 6px))';
      });

      chart.canvas.addEventListener('mouseleave', () => {
        getTooltipEl(chart).style.opacity = '0';
      });
    }
  },
};
